import { ArrowRight } from "lucide-react";
import { useGeoCurrency } from "@/hooks/useGeoCurrency";
import { Section, SectionHead } from "@/components/sn/SectionHead";

// Same words as before. The animated stage diagram is now one mono line; the
// learning points are a hairline list; the price sits in its own column.
export const learningPoints = [
  "Market research using AI (Perplexity, NotebookLM)",
  "User pain points & competition analysis",
  "Current trends & opportunity mapping",
  "Product roadmap creation",
  "Website building with no-code AI tools",
  "Going live & launch strategy",
];

export const stages = ["Idea", "Research", "Build", "Launch"];

export const SN_IdeaToLaunchSection = ({ label = "02 · Consultation service" }: { label?: string }) => {
  const { isIndian, servicePrices } = useGeoCurrency();
  const ctaHref = isIndian
    ? "/free-discovery-call"
    : `mailto:info@shikshannivesh.com?subject=${encodeURIComponent("Inquiry: Idea to Launch Consultation")}`;

  return (
    <Section id="consultation-services">
      <SectionHead
        label={label}
        title="Idea to launch consultation."
        lead="Turn your startup idea into a live product, guided by practitioners who build alongside you."
      />

      <p className="mt-8 font-mono text-[12.5px] tracking-[0.04em] text-muted-foreground">
        {stages.join("  →  ")}
      </p>

      <div className="mt-10 grid gap-12 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] lg:gap-14">
        <div>
          <p className="max-w-[62ch] text-[17px] leading-[1.65] text-[var(--sn-fg2)]">
            We&apos;re not gurus selling you a templated playbook. We&apos;re investors and practitioners who study
            markets, businesses, and companies daily. When you work with us, you get real-time collaboration: we
            learn and build alongside you, not lecture from theory.
          </p>

          <p className="sn-label mt-10">What you&apos;ll learn</p>
          <ul className="mt-2 list-none p-0">
            {learningPoints.map((point) => (
              <li key={point} className="flex items-start gap-3 border-t border-[var(--sn-border)] py-3 text-[15px] leading-[1.5] text-foreground">
                <ArrowRight className="mt-1 h-3.5 w-3.5 flex-shrink-0 text-primary" aria-hidden="true" />
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-[14px] border border-[var(--sn-border-strong)] bg-[hsl(var(--card))] p-6 lg:self-start">
          <p className="sn-label">Starting price</p>
          <p className="mt-3 text-[40px] font-semibold tracking-[-0.035em] text-foreground [font-variant-numeric:tabular-nums]">
            {servicePrices.ideaToLaunch}
          </p>
          <p className="mt-3 text-[13.5px] leading-[1.55] text-muted-foreground">
            Includes free discovery call to assess your idea and determine project scope. Final pricing varies
            based on complexity.
          </p>
          <a
            href={ctaHref}
            className="mt-5 inline-flex h-10 w-full items-center justify-center rounded-lg bg-primary px-[18px] text-[14px] font-semibold text-primary-foreground transition hover:brightness-110"
          >
            {isIndian ? "Book a free discovery call" : "Email us to get started"}
          </a>
        </div>
      </div>
    </Section>
  );
};

export default SN_IdeaToLaunchSection;
